import { useState, useEffect } from 'react';
const kudozLogoCircular = "/kudoz-icon-disc.png";

interface KudozModuleProps {
  earned: number;
  total?: number;
  showProgress?: boolean;
  animating?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function KudozModule({ 
  earned, 
  total, 
  showProgress = true,
  animating = false,
  size = 'md',
  className = ''
}: KudozModuleProps) {
  const safeEarned = total ? Math.min(earned, total) : earned;
  const progressPercent = total && total > 0 ? (safeEarned / total) * 100 : 0;

  const sizeConfig = {
    sm: { diameter: 'w-16 h-16', icon: 'w-5 h-5', text: 'text-xs', ringWidth: 2, ringRadius: 30 },
    md: { diameter: 'w-20 h-20', icon: 'w-6 h-6', text: 'text-sm', ringWidth: 3, ringRadius: 38 },
    lg: { diameter: 'w-24 h-24', icon: 'w-7 h-7', text: 'text-base', ringWidth: 3, ringRadius: 46 }
  };

  const config = sizeConfig[size];
  const circumference = 2 * Math.PI * config.ringRadius;
  const targetOffset = circumference - (progressPercent / 100) * circumference;

  // Start at full offset (empty ring) and animate to the target on mount / when target changes.
  // Using requestAnimationFrame ensures the CSS transition fires after the initial paint.
  const [renderedOffset, setRenderedOffset] = useState(circumference);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setRenderedOffset(targetOffset);
    });
    return () => cancelAnimationFrame(frame);
  }, [targetOffset]);

  return (
    <div className={`relative inline-flex ${className}`}>
      <div
        className={`relative ${config.diameter} rounded-full bg-background/95 backdrop-blur-md border border-border shadow-sm`}
        data-testid="kudoz-module"
      >
        {showProgress && total && total > 0 && (
          <svg
            className="absolute inset-0 w-full h-full -rotate-90"
            data-testid="kudoz-progress-bar"
          >
            <circle
              cx="50%"
              cy="50%"
              r={config.ringRadius}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth={config.ringWidth}
            />
            <circle
              cx="50%"
              cy="50%"
              r={config.ringRadius}
              fill="none"
              stroke="hsl(var(--kudoz))"
              strokeWidth={config.ringWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={renderedOffset}
              style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
            />
          </svg>
        )}

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <img src={kudozLogoCircular} alt="" className={config.icon} />
          <span
            key={safeEarned}
            className={`${config.text} font-bold text-kudoz tabular-nums leading-none${animating ? ' kudoz-pulse' : ''}`}
            data-testid="kudoz-earned"
          >
            {safeEarned}
          </span>
        </div>
      </div>
    </div>
  );
}
